
import React from "react";
import { Redirect } from "@/types/redirectTypes";
import { Button } from "@/components/ui/button";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { ScrollArea } from "@/components/ui/scroll-area";
import { 
  Edit, 
  Trash2, 
  AlertCircle, 
  CheckCircle 
} from "lucide-react";

interface RedirectsTableProps {
  redirects: Redirect[];
  isLoading: boolean;
  onEdit: (redirect: Redirect) => void;
  onDelete: (redirect: Redirect) => void;
}

const RedirectsTable: React.FC<RedirectsTableProps> = ({
  redirects,
  isLoading,
  onEdit,
  onDelete,
}) => {
  const formatDate = (dateString: string | undefined) => {
    if (!dateString) return "Never";
    return new Date(dateString).toLocaleDateString("en-US", {
      year: "numeric",
      month: "short",
      day: "numeric",
    });
  };

  const getStatusBadge = (statusCode: number) => {
    switch (statusCode) {
      case 301:
        return <Badge variant="default">301 Permanent</Badge>;
      case 302:
        return <Badge variant="secondary">302 Temporary</Badge>;
      case 307:
        return <Badge variant="secondary">307 Temporary</Badge>;
      case 308:
        return <Badge variant="default">308 Permanent</Badge>;
      default:
        return <Badge variant="outline">{statusCode}</Badge>;
    }
  };

  return (
    <div className="rounded-md border">
      <ScrollArea className="h-[600px]">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead className="w-[30%]">Source Path</TableHead>
              <TableHead className="w-[30%]">Target Path</TableHead>
              <TableHead className="w-[10%]">Status</TableHead>
              <TableHead className="w-[10%]">Active</TableHead>
              <TableHead className="w-[10%]">Hits</TableHead>
              <TableHead className="w-[10%] text-right">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {isLoading ? (
              <TableRow>
                <TableCell colSpan={6} className="text-center py-8 text-gray-500">
                  Loading redirects...
                </TableCell>
              </TableRow>
            ) : redirects.length === 0 ? (
              <TableRow>
                <TableCell colSpan={6} className="text-center py-8 text-gray-500">
                  No redirects found. Create your first redirect or import from CSV.
                </TableCell>
              </TableRow>
            ) : (
              redirects.map((redirect) => (
                <TableRow key={redirect.id}>
                  <TableCell className="font-medium">
                    {redirect.source_path}
                    {redirect.notes && (
                      <p className="text-xs text-gray-500 truncate mt-1">
                        {redirect.notes}
                      </p>
                    )}
                  </TableCell>
                  <TableCell>{redirect.target_path}</TableCell>
                  <TableCell>{getStatusBadge(redirect.status_code)}</TableCell>
                  <TableCell>
                    {redirect.is_active ? (
                      <div className="flex items-center">
                        <CheckCircle className="h-4 w-4 text-green-500 mr-1" />
                        <span className="text-sm">Active</span>
                      </div>
                    ) : (
                      <div className="flex items-center">
                        <AlertCircle className="h-4 w-4 text-gray-500 mr-1" />
                        <span className="text-sm">Inactive</span>
                      </div>
                    )}
                  </TableCell>
                  <TableCell>
                    <span className="font-mono">
                      {redirect.access_count}
                    </span>
                    {redirect.last_accessed_at && (
                      <p className="text-xs text-gray-500">
                        Last: {formatDate(redirect.last_accessed_at)}
                      </p>
                    )}
                  </TableCell>
                  <TableCell className="text-right">
                    <div className="flex justify-end space-x-1">
                      <Button
                        variant="ghost"
                        size="icon"
                        onClick={() => onEdit(redirect)}
                        className="h-8 w-8"
                      >
                        <Edit className="h-4 w-4" />
                        <span className="sr-only">Edit</span>
                      </Button>
                      <Button
                        variant="ghost"
                        size="icon"
                        onClick={() => onDelete(redirect)}
                        className="h-8 w-8 text-red-500 hover:text-red-600"
                      >
                        <Trash2 className="h-4 w-4" />
                        <span className="sr-only">Delete</span>
                      </Button>
                    </div>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </ScrollArea>
    </div>
  );
};

export default RedirectsTable;
